import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Droplets, Check, Heart, Trophy, CloudRain, Unlink } from "lucide-react";
import { Megaphone, Sparkles } from "@/lib/icons";
import { HandleAvatar } from "@/components/HandleAvatar";

const THANKS = ["🙏", "🌸", "💚"];

function WaterBack({ cheer, onDone }) {
  const [busy, setBusy] = useState(false);
  const water = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/community/members/${cheer.from_user}/water`);
      toast.success(`You watered ${cheer.from_handle}'s garden back — +1 each.`);
      if (r.data.achievement) toast.success(`Achievement unlocked: ${r.data.achievement.title} (+${r.data.achievement.points} pts)`);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't water back just now."); setBusy(false); }
  };
  return (
    <button onClick={water} disabled={busy} data-testid={`water-back-${cheer.id}`}
      className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60 transition-colors">
      <Droplets className="w-3.5 h-3.5" /> {busy ? "Watering…" : "Water back"}
    </button>
  );
}

/** Tiny thank-you back to the cheerer: pick one of three emoji, once per cheer. */
function ThanksPicker({ cheer, onDone }) {
  const [open, setOpen] = useState(false);
  const send = async (emoji) => {
    try {
      await api.post(`/buddies/cheers/${cheer.id}/thanks`, { emoji });
      onDone(emoji);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send thanks."); }
  };
  if (cheer.thanks) return <span className="text-xs font-semibold text-stone-500" data-testid={`thanked-${cheer.id}`}>Thanked {cheer.thanks}</span>;
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`thanks-${cheer.id}`} title="Say thanks"
        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border border-stone-200 text-stone-600 hover:border-rose-300 hover:text-rose-700 transition-colors">
        <Heart className="w-3.5 h-3.5" /> Thanks
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 rounded-full bg-white border border-stone-200 px-1 py-0.5" data-testid={`thanks-picker-${cheer.id}`}>
      {THANKS.map((e) => <button key={e} onClick={() => send(e)} data-testid={`thanks-emoji-${cheer.id}-${THANKS.indexOf(e)}`} className="text-base leading-none px-1.5 py-1 rounded-full hover:bg-rose-50 transition-colors">{e}</button>)}
    </div>
  );
}

function CheerText({ c }) {
  if (c.kind === "rain" || c.kind === "release") return <span className="text-stone-600"> · <span className="font-semibold text-sky-900">{c.message}</span></span>;
  if (c.kind === "challenge_nudge") return <span className="text-stone-600"> · <span className="font-semibold text-amber-900">{c.message}</span> <span className="text-xs text-stone-400">· {c.habit_title}</span></span>;
  if (c.kind === "thanks") return <span className="text-stone-600"> said thanks for {c.habit_title} <span className="text-base leading-none" data-testid="thanks-emoji">{c.message}</span></span>;
  if (c.kind === "water") return <span className="text-stone-600"> watered your garden · <span className="font-semibold text-sky-800">+1 pt</span></span>;
  const big = c.kind === "milestone" || c.kind === "weekly";
  return (
    <>
      <span className="text-stone-600"> cheered {big ? c.habit_title : "you on"}: </span>
      <span className="font-semibold text-emerald-800">{c.message}</span>
      {!big && <span className="text-xs text-stone-400"> · {c.habit_title}</span>}
    </>
  );
}

const ICONS = {
  challenge_nudge: <Trophy className="w-3.5 h-3.5 text-amber-700 inline mr-1.5 -mt-0.5" />,
  rain: <CloudRain className="w-3.5 h-3.5 text-sky-700 inline mr-1.5 -mt-0.5" />,
  release: <Unlink className="w-3.5 h-3.5 text-amber-800 inline mr-1.5 -mt-0.5" />,
  thanks: <Heart className="w-3.5 h-3.5 text-rose-600 inline mr-1.5 -mt-0.5" />,
  water: <Droplets className="w-3.5 h-3.5 text-sky-700 inline mr-1.5 -mt-0.5" />,
  milestone: <Sparkles className="w-3.5 h-3.5 text-amber-700 inline mr-1.5 -mt-0.5" />,
};
const TONE = {
  challenge_nudge: "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200",
  rain: "bg-gradient-to-r from-sky-50 via-cyan-50 to-sky-100 border-sky-200",
  release: "bg-amber-50 border-amber-200",
  thanks: "bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200",
  water: "bg-gradient-to-r from-sky-50 to-cyan-50 border-sky-200",
};

export function BuddyCheers({ onWaterBack }) {
  const [cheers, setCheers] = useState([]);
  useEffect(() => {
    api.get("/buddies/cheers").then((r) => setCheers(r.data)).catch(() => {});
  }, []);
  if (!cheers.length) return null;
  const extra = cheers.length - 3;
  const markRead = (id) => api.post(`/buddies/cheers/${id}/read`).catch(() => {});
  const dismiss = (id) => { markRead(id); setCheers((c) => c.filter((x) => x.id !== id)); };
  const patch = (pred, changes) => setCheers((all) => all.map((x) => (pred(x) ? { ...x, ...changes } : x)));
  const wateredBack = (c) => {
    markRead(c.id);
    patch((x) => x.kind === "water" && x.from_user === c.from_user, { can_water_back: false, watered_back: true });
    onWaterBack?.();
  };
  return (
    <div className="space-y-2" data-testid="buddy-cheers">
      <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800 px-1" data-testid="cheers-count">{cheers.length} new cheer{cheers.length === 1 ? "" : "s"} from your community</div>
      {cheers.slice(0, 3).map((c) => (
        <div key={c.id} className={`rounded-2xl p-4 border flex items-center gap-3 flex-wrap ${TONE[c.kind] || "bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200"}`} data-testid={`cheer-${c.id}`}>
          <HandleAvatar handle={c.from_handle} />
          <div className="flex-1 text-sm min-w-[12rem]">
            {ICONS[c.kind] || <Megaphone className="w-3.5 h-3.5 text-emerald-700 inline mr-1.5 -mt-0.5" />}
            <span className="font-semibold text-stone-900" data-testid="cheer-from">{c.from_handle}</span>
            <CheerText c={c} />
          </div>
          {c.kind !== "thanks" && c.kind !== "challenge_nudge" && c.kind !== "rain" && c.kind !== "release" && <ThanksPicker cheer={c} onDone={(emoji) => patch((x) => x.id === c.id, { thanks: emoji })} />}
          {c.kind === "water" && c.watered_back && <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-800" data-testid={`watered-back-${c.id}`}><Check className="w-3.5 h-3.5" /> Watered back</span>}
          {c.kind === "water" && c.can_water_back && <WaterBack cheer={c} onDone={() => wateredBack(c)} />}
          <button data-testid={`cheer-dismiss-${c.id}`} onClick={() => dismiss(c.id)} className="text-stone-400 hover:text-stone-700"><X className="w-4 h-4" /></button>
        </div>
      ))}
      {extra > 0 && <div className="text-xs text-stone-500 px-1">+{extra} more — dismiss a cheer to see the next</div>}
    </div>
  );
}
